import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import type { ToolExecutionContext } from '../types';

import { describeFolders, fetchUploadFolders } from './media-agent-utils';
import { Tool } from './Tool';

import type {
    AssetContainerModel,
    MediaFolder,
    MediaOptions,
    MediaSearchRequest,
} from 'js/src/client';
import { MediaSearchSourceEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';
import { MEDIA_LICENSE_OPTIONS } from 'js/src/data/media';
import { selectMediaOptions } from 'js/src/data/media-recommendation-preferences';

const RESULT_COUNT = 10;

type UploadFolderScope =
    | { kind: 'workspace'; folders: MediaFolder[] }
    | { kind: 'folders'; folders: MediaFolder[] }
    | { kind: 'error'; message: string };

function mediaTypeFilter(mediaType: MediaSearchArgs['media_type']): string {
    if (mediaType === 'images') {
        return 'photos';
    }
    if (mediaType === 'videos') {
        return 'videos';
    }
    return 'photos,videos';
}

// The user can restrict workspace media to specific folders in their media preferences;
// that restriction is a hard limit, and a folder the agent names narrows it further.
async function resolveUploadFolderScope(
    mediaOptions: MediaOptions,
    folderName: string | undefined,
    query: string,
): Promise<UploadFolderScope> {
    const restrictedToFolders =
        mediaOptions.workspace_media_mode === WorkspaceMediaModeEnum.FOLDERS;
    const needsFolders = restrictedToFolders || Boolean(folderName) || !query;
    if (!needsFolders) {
        return { kind: 'workspace', folders: [] };
    }

    const allFolders = await fetchUploadFolders();
    const allowedFolders = restrictedToFolders
        ? allFolders.filter((folder) => mediaOptions.workspace_folder_ids.includes(folder.id))
        : allFolders;

    if (restrictedToFolders && allowedFolders.length === 0) {
        return {
            kind: 'error',
            message:
                "The user's media preferences limit uploaded media to specific folders, but none of those folders exist anymore. Use another enabled media source, or ask the user to pick folders in their media preferences.",
        };
    }

    if (folderName) {
        const normalizedName = folderName.trim().toLowerCase();
        const match = allowedFolders.find(
            (folder) => folder.name.trim().toLowerCase() === normalizedName,
        );
        if (!match) {
            const available = allowedFolders.length
                ? `Available folders: ${describeFolders(allowedFolders)}.`
                : 'The user has no upload folders.';
            return {
                kind: 'error',
                message: `No upload folder named "${folderName}" is available. ${available}`,
            };
        }
        return { kind: 'folders', folders: [match] };
    }

    return restrictedToFolders
        ? { kind: 'folders', folders: allowedFolders }
        : { kind: 'workspace', folders: allFolders };
}

// Round-robin across per-folder results so one large folder doesn't crowd out the rest.
function interleave<T>(lists: T[][], limit: number): T[] {
    const merged: T[] = [];
    const longest = Math.max(0, ...lists.map((list) => list.length));
    for (let index = 0; index < longest && merged.length < limit; index++) {
        for (const list of lists) {
            if (index < list.length && merged.length < limit) {
                merged.push(list[index]);
            }
        }
    }
    return merged;
}

export const mediaSearchSchema = z.object({
    query: z
        .string()
        .optional()
        .describe(
            'The search query to find relevant media. Works best with detailed queries. Must be English. Required when source is "library". Optional when source is "uploads" — omit it to browse the user\'s uploads, or provide a query to filter them by name.',
        ),
    media_type: z
        .enum(['images', 'videos', 'all'])
        .default('all')
        .describe(
            'Type of media to search for. When searching stock for scene media, default to "videos" — video keeps scenes moving, and a stock photo is a last resort for when repeated video searches from different angles turn up nothing relevant. Use "images" or "all" when the user asks for them or the content genuinely calls for a still.',
        ),
    source: z
        .enum(['library', 'uploads'])
        .default('library')
        .describe(
            'Where to search. "library" (default) searches stock/library media. "uploads" searches only the media the user has previously uploaded to their account.',
        ),
    page: z
        .number()
        .int()
        .min(1)
        .default(1)
        .describe(
            'Page of results to return (10 per page). Increase it to keep browsing an uploads folder past the first results.',
        ),
    folder: z
        .string()
        .optional()
        .describe(
            'Only with source "uploads": the name of one of the user\'s upload folders to search within. Use it whenever the user points you at a specific folder. Without it, uploads in every folder are searched.',
        ),
});
export type MediaSearchArgs = z.infer<typeof mediaSearchSchema>;

export class MediaSearchTool extends Tool<typeof mediaSearchSchema> {
    protected override readonly schema = mediaSearchSchema;
    protected override readonly toolName = TOOL_NAMES.searchMedia;
    protected override readonly toolDescription =
        'Search for media assets (images, videos, or all types) using a text query. By default this searches the stock/library media.';

    public override canRunAsync = true;

    public override getToolDisplayName(): string {
        return 'Searching for media';
    }

    public get statusIcon(): string {
        return 'perm_media';
    }

    public async execute(args: MediaSearchArgs, _context: ToolExecutionContext) {
        const { media_type = 'all', source = 'library', page = 1 } = args;
        const query = args.query?.trim() ?? '';

        if (source !== 'uploads' && !query) {
            return 'Error: a search query is required when searching the media library. Provide a descriptive query, or set source to "uploads" to browse the user\'s uploaded media.';
        }

        // Respect the user's media-source preferences: don't search a source the
        // user has disabled. Return a message rather than results so the agent can
        // switch to an enabled source.
        const mediaOptions = selectMediaOptions(this._getState());
        if (source === 'uploads') {
            if (mediaOptions.workspace_media_mode === WorkspaceMediaModeEnum.OFF) {
                return 'Workspace/uploaded media is disabled by the user\'s media preferences. Search stock media with source "library" if it is enabled, or ask the user to enable workspace media.';
            }
        } else if (!mediaOptions.stock_media_enabled) {
            return 'Stock media is disabled by the user\'s media preferences. Use source "uploads" for the user\'s own media, or ask the user to enable workspace media.';
        }

        try {
            if (source === 'uploads') {
                return await this.searchUploads(query, media_type, page, args.folder, mediaOptions);
            }

            const filter = [
                mediaTypeFilter(media_type),
                MEDIA_LICENSE_OPTIONS.EDITORIAL,
                MEDIA_LICENSE_OPTIONS.STOCK,
                'basic,premium,marketplace',
            ].join(',');

            const results = await this.search({
                q: query,
                search_source: MediaSearchSourceEnum.RECOMMENDED,
                page,
                filter,
                // Once the team is nearly out of marketplace credits, stop recommending
                // credit-costing stock so the agent doesn't steer the user into the paywall.
                filter_out_premium_media_if_exceeding_credits_limit: true,
                min_suitability_score: mediaOptions.minimum_stock_suitability_score ?? undefined,
            });
            return JSON.stringify(this.registerResults(results));
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error searching for media: ${errorMessage}`;
        }
    }

    private async searchUploads(
        query: string,
        mediaType: MediaSearchArgs['media_type'],
        page: number,
        folderName: string | undefined,
        mediaOptions: MediaOptions,
    ): Promise<string> {
        const scope = await resolveUploadFolderScope(mediaOptions, folderName, query);
        if (scope.kind === 'error') {
            return scope.message;
        }

        // License/marketplace filters are meaningless for the user's own uploads, and the
        // low-credit check costs DB queries, so only the type filter applies.
        const filter = mediaTypeFilter(mediaType);
        const searchFolder = (folderId: number | null, folderPage = page, count = RESULT_COUNT) =>
            this.search({
                q: query,
                search_source: MediaSearchSourceEnum.UPLOADS,
                page: folderPage,
                count,
                filter,
                folder_id: folderId,
                // Without this, a query-less browse only returns uploads outside any folder.
                include_all_folders: folderId === null,
                filter_out_premium_media_if_exceeding_credits_limit: false,
            });

        if (scope.kind === 'folders' && scope.folders.length === 1) {
            return JSON.stringify(this.registerResults(await searchFolder(scope.folders[0].id)));
        }

        if (scope.kind === 'folders') {
            // Each folder pages independently, so to return a stable slice of the merged list
            // fetch every earlier page too — otherwise the tail of each folder's page is skipped.
            const mergedCount = page * RESULT_COUNT;
            const perFolderResults = await Promise.all(
                scope.folders.map((folder) => searchFolder(folder.id, 1, mergedCount)),
            );
            const merged = interleave(perFolderResults, mergedCount);
            return JSON.stringify(
                this.registerResults(merged.slice(mergedCount - RESULT_COUNT, mergedCount)),
            );
        }

        const representations = JSON.stringify(this.registerResults(await searchFolder(null)));
        // Organized users keep their media in folders, so let the agent narrow to one.
        if (!query && scope.folders.length > 0) {
            return `${representations}\n\nThe user's uploads are organized into these folders: ${describeFolders(scope.folders)}. Pass \`folder\` to browse or search just one.`;
        }
        return representations;
    }

    private async search(
        body: Omit<MediaSearchRequest, 'autocorrect' | 'did_you_mean'>,
    ): Promise<AssetContainerModel[]> {
        const response = await ApiService.mediaSearchCreate({
            body: {
                count: RESULT_COUNT,
                ...body,
                autocorrect: false,
                did_you_mean: false,
            },
        });
        return response.data.results;
    }

    private registerResults(results: AssetContainerModel[]) {
        const mediaRegistry = MediaRegistry.getInstance();
        return results.map((result) =>
            mediaRegistry.getRepresentation(mediaRegistry.addItem(result)),
        );
    }
}
